// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useActions } from '../hooks/useActions';
import { useTypedSelector } from '../hooks/useTypedSelector';
import SelectProject from './SelectProject';
import SelectProjectRelease from './SelectProjectRelease';
import { ITempProjectModel } from '../models/TempProjectModel';
import { IProjectReleaseModel } from '../models/ProjectReleaseModel';
import { ThemeProvider } from '@emotion/react';
import { Button, Dialog, DialogContent, Paper } from '@mui/material';
import { theme } from '../styles/ButtonTextStyle';

interface Props {
  projects: ITempProjectModel[];
  selectedProjectId: number | null;
  selectedReleaseId: number | null;
  onConfirm: (projectId: number, releaseId: number | null) => void;
  projectName?: string;
  routes: string;
  closeModal: () => void;
}

const ProjectReleaseModal: React.FC<Props> = ({
  projects,
  selectedProjectId,
  onConfirm,
  selectedReleaseId,
  projectName,
  routes,
  closeModal,
}) => {
  const { fetchProject } = useActions();
  const { project, loading: isProjectLoading } = useTypedSelector((state) => state.project);

  const initialProject = projects.find((p) => p.id === selectedProjectId) ?? projects[0];
  const [localProjectId, setLocalProjectId] = useState<number | null>(initialProject?.id ?? null);
  const [localReleaseId, setLocalReleaseId] = useState<number | null>(null);

  const isValidProjectId = projects.some((p) => p.id === selectedProjectId);
  const [currentProjectName, setCurrentProjectName] = useState(() =>
    isValidProjectId ? projectName : (initialProject?.projectName ?? '')
  );
  const [showProjectModal, setShowProjectModal] = useState<boolean>(
    !selectedProjectId || !isValidProjectId
  );

  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (localProjectId) {
      fetchProject(localProjectId);
    }
  }, [localProjectId]);

  const [releases, setReleases] = useState<IProjectReleaseModel[]>([]);

  useEffect(() => {
    if (project && project.id === localProjectId) {
      const projectReleases = project.projectReleases || [];
      setReleases(projectReleases);
      if (projectReleases.length === 0 || localReleaseId === null) {
        setLocalReleaseId(0);
      }
    }
  }, [project, localProjectId]);

  useEffect(() => {
    if (
      selectedProjectId &&
      projects.length > 0 &&
      projects.some((p) => p.id === selectedProjectId)
    ) {
      const selectedProject = projects.find((p) => p.id === selectedProjectId);
      setLocalProjectId(selectedProjectId);
      setCurrentProjectName(selectedProject?.projectName || '');
      setShowProjectModal(false);
    } else if (
      !selectedProjectId ||
      (projects.length > 0 && !projects.some((p) => p.id === selectedProjectId))
    ) {
      const fallbackProject = projects[0];
      if (fallbackProject) {
        setLocalProjectId(fallbackProject.id);
        setCurrentProjectName(fallbackProject.projectName);
      }
      setShowProjectModal(true);
    }
  }, [selectedProjectId, projects]);

  const handleProjectChange = (id: number) => {
    setLocalProjectId(id);
    setLocalReleaseId(null);
    setReleases([]);
    setCurrentProjectName(projects.find((project) => project.id === id)?.projectName ?? '');
  };

  const handleOk = () => {
    if (localProjectId !== null && localReleaseId !== null) {
      onConfirm(localProjectId, localReleaseId);
      handleClose();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const isOkDisabled =
    !localProjectId || isProjectLoading || (releases.length > 0 && localReleaseId === null);
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      TransitionProps={{
        onExited: closeModal,
      }}
      PaperComponent={(props) => (
        <Paper
          {...props}
          className="dialog-card"
          sx={{
            borderRadius: '8px',
          }}
        />
      )}
    >
      <DialogContent sx={{ m: 0 }}>
        {showProjectModal && (
          <>
            <h2 className="dialog-subheading">Select Project</h2>

            <div className="dialog-selector">
              <SelectProject
                projects={projects}
                currentProjectName={currentProjectName}
                renderContent={false}
                onChange={handleProjectChange}
                selectProjectId={localProjectId ?? 0}
              />
            </div>
          </>
        )}

        {!isProjectLoading && releases.length > 0 && (
          <>
            <h2 className="dialog-subheading">
              Select Release
              {currentProjectName && (
                <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>
                  for "{currentProjectName}"
                </span>
              )}
            </h2>
            <div className="dialog-selector">
              <SelectProjectRelease
                releases={releases}
                onChange={(id: number) => {
                  setLocalReleaseId(id);
                }}
                defaultReleaseId={localReleaseId ?? 0}
                routes={routes}
                renderContent={false}
                disablePortal
              />
            </div>
          </>
        )}

        <ThemeProvider theme={theme}>
          <div className="dialog-buttons">
            <Button onClick={handleOk} disabled={isOkDisabled} className="dialog-button-ok">
              OK
            </Button>
          </div>
        </ThemeProvider>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectReleaseModal;
